import { motion } from 'framer-motion';
import { Clock, Users, Calendar, BookOpen, Star, MapPin } from 'lucide-react';
import { Course } from '../services/courseService';

function openCourseInNewTab(schoolSlug: string, courseSlug: string) {
  if (!schoolSlug || !courseSlug) return;
  const url = `${window.location.origin}/schools/${encodeURIComponent(schoolSlug)}/${encodeURIComponent(courseSlug)}`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

interface CourseCardsProps {
  courses: Course[];
  schoolSlug: string;
  schoolLocation?: string;
  schoolCity?: string;
  schoolImages?: {
    gallery?: string[];
    coverImage?: string;
    logo?: string;
  };
  loading?: boolean;
}

const CourseCards: React.FC<CourseCardsProps> = ({ 
  courses, 
  schoolSlug, 
  schoolLocation,
  schoolCity,
  schoolImages,
  loading = false,
}) => {
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const getDifficultyColor = (difficulty?: string) => {
    switch (difficulty) {
      case 'BEGINNER':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'INTERMEDIATE':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'ADVANCED':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-blue-100 text-blue-800 border-blue-200';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'TRAINING':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'RETREAT':
        return 'bg-pink-100 text-pink-800 border-pink-200';
      case 'WORKSHOP':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'CLASS':
        return 'bg-cyan-100 text-cyan-800 border-cyan-200';
      default:
        return 'bg-primary/10 text-primary border-primary/20';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!courses || courses.length === 0) {
    return (
      <div className="text-center py-8">
        <BookOpen className="h-10 w-10 text-ivory-400 mx-auto mb-3" />
        <p className="text-ivory-600 text-2">No courses available at the moment.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-4">
      {courses.map((course, index) => (
          <motion.div
            key={course._id}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          className="w-full md:w-[calc(50%-0.5rem)] lg:w-[calc(33.333%-0.67rem)] bg-white rounded-xl shadow-lg border border-ivory-200 overflow-hidden hover:shadow-xl transition-all duration-300 flex flex-col group cursor-pointer relative min-h-0"
            onClick={() => openCourseInNewTab(schoolSlug, course.slug)}
          >
            {/* Course Image */}
          <div className="relative h-36 bg-gradient-to-br from-primary/20 to-primary/40 overflow-hidden">
            {course.media?.images && course.media.images.length > 0 ? (
                <img 
                src={course.media.images[0]} 
                  alt={course.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  onError={(e) => {
                    // Prevent infinite loops by checking if already retried
                    const target = e.target as HTMLImageElement;
                    const hasRetried = target.dataset.retried === 'true';
                    
                    if (hasRetried) {
                      // Already tried fallback, hide image and show placeholder
                      target.style.display = 'none';
                      target.parentElement?.querySelector('.placeholder')?.classList.remove('hidden');
                      e.stopPropagation();
                      return;
                    }
                    
                    // Mark as retried and try fallback
                    target.dataset.retried = 'true';
                    
                    if (schoolImages?.gallery && schoolImages.gallery.length > 0) {
                      target.src = schoolImages.gallery[index % schoolImages.gallery.length];
                    } else if (schoolImages?.coverImage) {
                      target.src = schoolImages.coverImage;
                    } else {
                      target.style.display = 'none';
                      target.parentElement?.querySelector('.placeholder')?.classList.remove('hidden');
                    }
                    e.stopPropagation();
                  }}
                />
              ) : schoolImages?.gallery && schoolImages.gallery.length > 0 ? (
                <img 
                  src={schoolImages.gallery[index % schoolImages.gallery.length]} 
                  alt={course.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    target.parentElement?.querySelector('.placeholder')?.classList.remove('hidden');
                    e.stopPropagation();
                  }}
                />
              ) : schoolImages?.coverImage ? (
                <img 
                  src={schoolImages.coverImage} 
                  alt={course.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    target.parentElement?.querySelector('.placeholder')?.classList.remove('hidden');
                    e.stopPropagation();
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center placeholder">
                <BookOpen className="h-12 w-12 text-primary/50" />
                </div>
              )}
            <div className="absolute top-2 right-2 flex flex-col gap-1.5 z-20">
              {course.difficulty && (
                <span className={`px-2 py-0.5 rounded-full text-1 font-semibold border backdrop-blur-sm ${getDifficultyColor(course.difficulty)}`}>
                    {course.difficulty}
                  </span>
              )}
              <span className={`px-2 py-0.5 rounded-full text-1 font-semibold border backdrop-blur-sm ${getTypeColor(course.type)}`}>
                {course.type}
              </span>
            </div>
              {course.rating > 0 && (
              <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-white/90 backdrop-blur-sm px-1.5 py-0.5 rounded-full">
                  <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                  <span className="text-1 font-semibold text-ivory-900">{course.rating.toFixed(1)}</span>
                </div>
              )}
            </div>

            {/* Course Content */}
          <div className="p-3.5 sm:p-4 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
            <h3 className="text-2 sm:text-3 font-semibold text-ivory-900 mb-1 group-hover:text-primary transition-colors line-clamp-2 leading-snug">
              {course.name}
            </h3>
              
              {/* Short Description */}
              {course.shortDescription && (
              <p className="text-ivory-600 text-1 mb-2 line-clamp-2">
                  {course.shortDescription}
                </p>
              )}

              {/* Course Details */}
            <div className="space-y-1.5 mb-2">
              <div className="flex items-center gap-1.5 text-ivory-700 text-1">
                <Clock className="h-3 w-3 text-primary flex-shrink-0" />
                <span>{course.duration} days</span>
              </div>
              {course.maxStudents && (
                <div className="flex items-center gap-1.5 text-ivory-700 text-1">
                  <Users className="h-3 w-3 text-primary flex-shrink-0" />
                  <span>Max {course.maxStudents} students</span>
                </div>
              )}
              {course.schedule?.startDate && (
                <div className="flex items-center gap-1.5 text-ivory-700 text-1">
                  <Calendar className="h-3 w-3 text-primary flex-shrink-0" />
                  <span>Starts {formatDate(course.schedule.startDate)}</span>
                </div>
              )}
              {(schoolLocation || schoolCity) && (
                <div className="flex items-center gap-1.5 text-ivory-700 text-1">
                  <MapPin className="h-3 w-3 text-primary flex-shrink-0" />
                  <span>{schoolLocation || schoolCity}</span>
                </div>
              )}
            </div>

            {/* Pricing + CTA pinned to bottom of card body */}
            <div className="mt-auto shrink-0 pt-2 border-t border-ivory-200 space-y-2">
              <div>
                {course.priceRange && course.priceRange.min !== undefined ? (
                <div className="flex items-baseline gap-1.5">
                  <span className="text-4 font-bold text-primary">
                    {formatCurrency(course.priceRange.min, course.priceRange.currency)}
                  </span>
                  <span className="text-1 text-ivory-600">for {course.duration} days</span>
                </div>
              ) : course.price ? (
                <div className="flex items-baseline gap-1.5">
                  <span className="text-4 font-bold text-primary">
                      {formatCurrency(course.price, course.currency)}
                    </span>
                  <span className="text-1 text-ivory-600">for {course.duration} days</span>
                  </div>
                ) : (
                <span className="text-ivory-600 text-1">Contact for pricing</span>
                )}
              </div>

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  openCourseInNewTab(schoolSlug, course.slug);
                }}
              className="w-full bg-primary text-white py-2 rounded-lg font-semibold hover:bg-primary-dark transition-colors shadow-md flex items-center justify-center gap-1.5 text-2"
              >
              <BookOpen className="h-3 w-3" />
                View Course Details
              </button>
            </div>
            </div>
          </motion.div>
      ))}
    </div>
  );
};

export default CourseCards;
